import React from 'react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import { CardSlot } from '@/components/dashboard/DashboardCard'
import SectionHeading from '@/components/dashboard/SectionHeading'
import MonthOnTheBooks, { type MonthOnTheBooksData } from '@/components/dashboard/MonthOnTheBooks'
import PickupTable, { type PickupRow } from '@/components/dashboard/PickupTable'
import PaceBand, { type PaceData } from '@/components/dashboard/PaceBand'
import TonightStrip, { type TodaySnapshotData } from '@/components/dashboard/TonightStrip'
import TrendCharts, { type TrendDataPoint, type PricedAtListings } from '@/components/dashboard/TrendCharts'
import ByListingCard, { type ByListingData } from '@/components/dashboard/ByListingCard'
import SoftDatesCard, { type SoftDatesData } from '@/components/dashboard/SoftDatesCard'
import RateHealthCard, { type RateHealthProps } from '@/components/dashboard/RateHealthCard'
import RecentActivityCard from '@/components/dashboard/RecentActivityCard'
import SyncStatusCard from '@/components/dashboard/SyncStatusCard'
import PaceChart from '@/components/dashboard/PaceChart'
import UpcomingEventsCard from '@/components/dashboard/UpcomingEventsCard'
import TargetOutlookCard, { type TargetOutlookData } from '@/components/dashboard/TargetOutlookCard'
import ClearanceMorningAfter, { type ClearanceMorningAfterData } from '@/components/dashboard/ClearanceMorningAfter'
import PropertySetupCard, { type PropertySetupData } from '@/components/dashboard/PropertySetupCard'
import GoLiveControl from '@/components/GoLiveControl'
import UnitScopeNote from '@/components/groups/UnitScopeNote'
import ManagedAtLeadCard from '@/components/groups/ManagedAtLeadCard'
import { isDeferredScope, groupHotelCount, leadLabel, leadPath } from '@/lib/reporting-scope'
import { AlertTriangle, ArrowRight, Crown } from 'lucide-react'
import { Link } from '@inertiajs/react'
import type { SharedProps, SyncStatus } from '@/types/shared'

/**
 * The revenue manager's dashboard.
 *
 * Ordered by how far ahead the decision reaches, not by how easy the number was to
 * compute: the whole month on the books first, then what moved this week, then how
 * the next six months pace against last year, and only then tonight's front-desk
 * position. Operations sit at the bottom because they are checked, not steered by.
 *
 * Every monetary figure is printed exactly as the backend sent it through
 * `formatMoney` — no rounding, no derivation (CLAUDE.md — "Data Integrity").
 *
 * GROUPS. `reportingScope` says whose numbers these are. At a LEAD every figure here covers the
 * whole unit (pooled bookings over pooled capacity), so the page says so once at the top. At a
 * MEMBER the revenue, pace and target props arrive `null`/`[]` DELIBERATELY — they live at the lead,
 * and shipping zeros would read as a hotel that has stopped selling. A member therefore gets its own
 * page (`MemberDashboard`) built from what it genuinely owns: its PMS connection, its sync and push
 * health, its unpublished rows and its room list — plus a pointer to the lead for everything else.
 * Ungrouped: `reportingScope` is null and nothing below changes.
 */

interface ActivityEntry {
  id: number
  /** Which hotel the change landed on. Always sent; only meaningful once a list spans hotels. */
  propertyId?: number
  propertyName?: string | null
  roomType: string
  ratePlan: string
  date: string
  oldRate: number
  newRate: number
  changePct: number
  source: string
  createdAt: string
}

interface PaceDayData {
  occupancyPct: number
  baselineOccPct: number | null
  paceRatio: number | null
}

interface EventEntry {
  id: number
  name: string
  startDate: string
  endDate: string
  impactLevel: string
  demandMultiplier: number
}

/**
 * Every revenue prop is nullable, and for two different reasons — both real:
 *   * a failed insights run degrades the band rather than 500-ing a read surface, and
 *   * a grouped MEMBER is sent null on purpose, because the figure lives at the lead.
 * `reportingScope` is what tells the two apart, and the page must never dereference one blind.
 */
interface DashboardProps extends SharedProps {
  todaySnapshot: TodaySnapshotData | null
  /** Null when the insights mutation failed, or when this property is a group member. */
  monthOnTheBooks: MonthOnTheBooksData | null
  pickup: PickupRow[]
  /**
   * A combined inventory's listings, each by name, for the pickup windows. Null for an ungrouped
   * hotel, a combined inventory reduced to its lead, a following listing (its numbers live at the
   * lead) and a failed run.
   */
  byListing?: ByListingData | null
  pace: PaceData | null
  trendData: TrendDataPoint[]
  /** "Priced at" per listing at a combined inventory; null everywhere the single series stands. */
  pricedAtListings?: PricedAtListings | null
  softDates: SoftDatesData | null
  rateHealth: RateHealthProps | null
  recentActivity: ActivityEntry[]
  syncStatus: SyncStatus
  paceData: Record<string, PaceDayData>
  upcomingEvents: EventEntry[]
  dirtyRatesCount: number
  targetOutlook?: TargetOutlookData | null
  /**
   * Last-Minute Clearance's morning-after line — one backend-composed sentence about LAST night,
   * or null when the rule is off, nothing cleared, or this is a group member (pricing lives at
   * the lead). Never routed to the layout's warning rail: it is a readout, not a fault.
   */
  clearanceMorningAfter?: ClearanceMorningAfterData | null
  propertySetup?: PropertySetupData | null
}

/** Shown in place of a band whose backend figures could not be built this request. */
function BandUnavailable({ what }: { what: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
      <AlertTriangle className="mx-auto h-8 w-8 text-muted-foreground/40" />
      <p className="mt-3 text-sm font-medium text-foreground">{what} couldn't be calculated</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Nothing is wrong with your bookings — these figures are rebuilt on every visit, so
        reloading the page usually brings them back.
      </p>
    </div>
  )
}

/**
 * A MEMBER'S DASHBOARD.
 *
 * A member of a group is not a hotel with no numbers — it is a hotel whose numbers are kept one
 * hotel over, because the group is measured and priced as one revenue unit at its lead. So this page
 * shows exactly what a member genuinely owns and nothing it does not:
 *
 *   * its own live/paused gate and PMS connection health (publishing is per listing — never pooled),
 *   * its own unpublished-rows count,
 *   * its own room list as the PMS has it,
 *
 * and it points at the lead, by name, for every figure and every control that lives there. It is
 * deliberately NOT the ordinary dashboard with empty cards: a blank band reads as a fault, and a
 * zero reads as a hotel that stopped selling. Neither is true.
 */
function MemberDashboard({
  property,
  scope,
  syncStatus,
  dirtyRatesCount,
  propertySetup,
}: {
  property: SharedProps['property']
  scope: NonNullable<SharedProps['reportingScope']>
  syncStatus: SyncStatus
  dirtyRatesCount: number
  propertySetup?: PropertySetupData | null
}) {
  const hotels = groupHotelCount(scope)

  // Everything a member has no page of its own for. Each row is a real hop to the hotel that owns
  // the setting — the pricing URLs redirect there anyway, so naming the destination first is the
  // difference between a redirect that explains itself and one that feels like a bug.
  const atTheLead: { label: string; description: string; suffix: string }[] = [
    {
      label: 'Revenue and pace',
      description: `On the books, pickup, ADR and RevPAR for all ${hotels === 1 ? '1 listing' : `${hotels} listings`} together`,
      suffix: '',
    },
    { label: 'Pricing rules', description: 'Base rates, seasons, guardrails and the room mapping', suffix: '/pricing-rules/rooms-setup' },
    { label: 'Targets', description: 'One occupancy target per month for the whole building', suffix: '/targets' },
    { label: 'Overrides', description: 'Manual prices and restrictions, including this listing’s rooms', suffix: '/overrides' },
    { label: 'History', description: 'Every price the engine moved and every rule someone changed, labelled by listing', suffix: '/history/prices' },
    { label: 'Market Intelligence', description: 'The compset the whole building prices against', suffix: '/market-intelligence' },
  ]

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold tracking-tight text-foreground">Dashboard</h1>
          <HelpTooltip
            content={`${property.name} is a following listing of ${scope.groupName ?? 'a combined inventory'}, so this page covers only what is its own: its PMS connection, its publishing and its rooms.`}
            learnMore="/guides/dashboard"
          />
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          What {property.name} publishes to its own PMS listing
        </p>
      </div>

      {/* The member's own live/paused gate — publishing is per listing, never pooled. */}
      <GoLiveControl propertyBasePath={property.basePath} />

      {/* Where the numbers went. First thing on the page, because it is the first question. */}
      <ManagedAtLeadCard
        scope={scope}
        what="Revenue, pace and targets"
        detail={`Bookings on every listing count toward the building, so revenue, pace and targets are reported once, at ${leadLabel(scope)}. ${property.name} keeps its own PMS listing and its own rooms.`}
        ctaLabel={`Open ${leadLabel(scope)}`}
      />

      {/* ── What this hotel owns ──────────────────────────────────────────────── */}
      <section>
        <SectionHeading
          title={`${property.name}’s own`}
          subtitle="Kept per listing"
          help={{
            content:
              'Each listing publishes its own prices to the PMS through its own connection and its own live or paused switch.',
          }}
        />
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          {syncStatus && (
            <CardSlot delay={0}>
              <SyncStatusCard
                syncStatus={syncStatus}
                propertyBasePath={property.basePath}
                dirtyRatesCount={dirtyRatesCount}
              />
            </CardSlot>
          )}

          {/* The hop list. Rendered as a card so it sits beside sync health rather than
              competing with it, and every row is an <a> (Inertia Link), never a button. */}
          <CardSlot delay={50}>
            <div className="flex h-full flex-col rounded-xl border border-border/60 bg-white shadow-sm">
              <div className="border-b border-border/40 px-5 py-4">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                  <Crown className="h-3.5 w-3.5 text-primary" />
                  Managed at {leadLabel(scope)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  The lead listing, where prices for this combined inventory are set
                </p>
              </div>
              <div className="divide-y divide-border/30">
                {atTheLead.map((row) => {
                  const href = leadPath(scope, row.suffix)
                  const text = (
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium text-foreground">{row.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">{row.description}</span>
                    </span>
                  )
                  // No link when the person cannot open the lead (change safety R4.3): the row still
                  // says what is managed there.
                  if (!href) {
                    return (
                      <div key={row.suffix || 'dashboard'} className="flex items-center gap-3 px-5 py-2.5">
                        {text}
                      </div>
                    )
                  }
                  return (
                    <Link
                      key={row.suffix || 'dashboard'}
                      href={href}
                      className="flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-muted/30"
                    >
                      {text}
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                    </Link>
                  )
                })}
              </div>
            </div>
          </CardSlot>

          {/* Its rooms, as the PMS has them — genuinely this hotel's, and the one inventory
              surface a member still needs (the ladder that prices them lives at the lead). */}
          {propertySetup && (
            <CardSlot delay={100} className="lg:col-span-2">
              <PropertySetupCard
                propertySetup={propertySetup}
                propertyBasePath={property.basePath}
                lastStructureSync={syncStatus?.lastStructureSync ?? null}
              />
            </CardSlot>
          )}
        </div>
      </section>
    </div>
  )
}

export default function Dashboard({
  currentUser,
  property,
  properties,
  todaySnapshot,
  monthOnTheBooks,
  pickup,
  byListing,
  pace,
  trendData,
  pricedAtListings,
  softDates,
  rateHealth,
  recentActivity,
  syncStatus,
  paceData,
  upcomingEvents,
  dirtyRatesCount,
  targetOutlook,
  clearanceMorningAfter,
  propertySetup,
  reportingScope,
}: DashboardProps) {
  // A MEMBER'S revenue props are null BY CONTRACT, not by failure — so it gets its own page rather
  // than this one with holes in it. Branch before anything dereferences a figure.
  if (isDeferredScope(reportingScope)) {
    return (
      <OperatorLayout title="Dashboard" currentUser={currentUser} property={property} properties={properties}>
        <MemberDashboard
          property={property}
          scope={reportingScope}
          syncStatus={syncStatus}
          dirtyRatesCount={dirtyRatesCount}
          propertySetup={propertySetup}
        />
      </OperatorLayout>
    )
  }

  const cur = todaySnapshot?.currency || property.currency
  const hasPickup = pickup?.length > 0
  const hasPace = Boolean(pace && pace.months.length > 0)
  const hasTrend = trendData?.length > 0
  const hasBookingPace = paceData && Object.keys(paceData).length > 0
  const unitHotels = groupHotelCount(reportingScope)

  return (
    <OperatorLayout title="Dashboard" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-8">
        {/* Page header */}
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Dashboard</h1>
            <HelpTooltip
              content="Your property's revenue position, from the whole month on the books down to tonight."
              learnMore="/guides/dashboard"
            />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {unitHotels > 1
              ? `Where ${reportingScope!.groupName} stands today, and what changed this week`
              : `Where ${property.name} stands today, and what changed this week`}
          </p>
        </div>

        {/* At a LEAD: every figure below is the unit's. Said once, calmly, before the numbers. */}
        <UnitScopeNote
          scope={reportingScope}
          covers="Revenue, occupancy, pace and targets"
          listHotels
        />

        {/* claim-15: per-property Go-live control (owner/admin only; self-hides otherwise) */}
        <GoLiveControl propertyBasePath={property.basePath} />

        {/* Target Outlook — opt-in; only mounts when targets are saved */}
        <CardSlot delay={0}>
          <TargetOutlookCard targetOutlook={targetOutlook ?? null} propertyBasePath={property.basePath} />
        </CardSlot>

        {/* ── Band 1: this month on the books ─────────────────────────────────── */}
        {monthOnTheBooks ? (
          <MonthOnTheBooks month={monthOnTheBooks} />
        ) : (
          <BandUnavailable what="This month's figures" />
        )}

        {/* ── Band 2: pickup & on the books ───────────────────────────────────── */}
        {hasPickup && (
          <CardSlot delay={0}>
            <PickupTable rows={pickup} />
          </CardSlot>
        )}

        {/* ── Band 2b: by listing (combined inventory only) ───────────────────
             Every band around it is the building. This is where each listing's own share of
             it shows, by name, for the same windows as the pickup table. */}
        {byListing && byListing.windows.length > 0 && (
          <CardSlot delay={50}>
            <ByListingCard data={byListing} />
          </CardSlot>
        )}

        {/* ── Band 3: pace against last year ──────────────────────────────────── */}
        {hasPace && (
          <CardSlot delay={0}>
            <PaceBand pace={pace!} propertyBasePath={property.basePath} />
          </CardSlot>
        )}

        {/* ── Band 4: tonight (demoted from five hero tiles to one strip) ─────── */}
        {todaySnapshot ? (
          <CardSlot delay={0}>
            <TonightStrip snapshot={todaySnapshot} propertyBasePath={property.basePath} />
          </CardSlot>
        ) : (
          <BandUnavailable what="Tonight's position" />
        )}

        {/* Last night, beside tonight — the only reporting Last-Minute Clearance ships. Self-hides
            when the rule is off or nothing cleared. */}
        <ClearanceMorningAfter line={clearanceMorningAfter} propertyBasePath={property.basePath} />

        {/* ── Band 5: needs a decision ────────────────────────────────────────
             Where reporting turns into an action list. Soft dates says which
             nights are behind their own curve; rate health says whether a
             guardrail — not demand — is setting the price. Both are read
             together: a soft night whose rate is already welded to its floor
             is a guardrail problem, not a demand problem. ──────────────────── */}
        <section>
          <SectionHeading
            title="Needs a decision"
            subtitle="Where you could still move the number"
            help={{
              content:
                "Soft dates are nights running behind this property's own booking curve, ranked by the room-nights still recoverable. Rate health says how much of the next month is pinned to a guardrail rather than being priced by demand.",
              learnMore: '/guides/occupancy-metrics',
            }}
          />
          {/* 2:1, not 1:1 — soft dates is a five-column table and rate health is three
              rows. An even split clipped the rooms-behind column, which is the one
              number the card exists to show. */}
          <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-3">
            <CardSlot delay={0} className="xl:col-span-2">
              {softDates ? (
                <SoftDatesCard soft={softDates} propertyBasePath={property.basePath} />
              ) : (
                <BandUnavailable what="Soft dates" />
              )}
            </CardSlot>
            {/* `h-auto` beats CardSlot's `h-full` through twMerge: rate health is three
                rows and would otherwise be stretched to the height of an eight-row soft
                dates table, leaving most of a card empty. */}
            <CardSlot delay={50} className="h-auto">
              {/* Spread, never `{...null}` guessing: an absent payload renders the explanation,
                  not a card full of `undefined`. */}
              {rateHealth ? <RateHealthCard {...rateHealth} /> : <BandUnavailable what="Rate health" />}
            </CardSlot>
          </div>
        </section>

        {/* ── Near-term detail: the next 7 nights, and the next 30 against the
             property's own booking curve. Distinct from Band 3, which is
             year-on-year over months. ─────────────────────────────────────────── */}
        {(hasTrend || hasBookingPace) && (
          <section>
            <SectionHeading
              title="Near-term detail"
              subtitle="The next few weeks, night by night"
              help={{
                content:
                  pricedAtListings
                    ? "Occupancy and revenue are confirmed bookings across the building. \"Priced at\" is the list price the engine is asking at each listing, not ADR. Booking pace compares each night with how full it usually is this far out."
                    : "Occupancy and revenue are confirmed bookings. \"Priced at\" is the list price the engine is asking, not ADR. Booking pace compares each night with how full it usually is this far out.",
                learnMore: '/guides/occupancy-metrics',
              }}
            />
            <div className="space-y-4">
              {hasTrend && <TrendCharts data={trendData} currency={cur} pricedAtListings={pricedAtListings} />}
              {hasBookingPace && (
                <CardSlot delay={150}>
                  <PaceChart paceData={paceData} />
                </CardSlot>
              )}
            </div>
          </section>
        )}

        {/* ── Band 6: operations ──────────────────────────────────────────────── */}
        <section>
          <SectionHeading
            title="Operations"
            subtitle="Events, PMS sync and what the engine changed"
          />
          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
            <CardSlot delay={0}>
              <UpcomingEventsCard events={upcomingEvents} />
            </CardSlot>
            {syncStatus && (
              <CardSlot delay={50}>
                <SyncStatusCard
                  syncStatus={syncStatus}
                  propertyBasePath={property.basePath}
                  dirtyRatesCount={dirtyRatesCount}
                />
              </CardSlot>
            )}
            <CardSlot delay={100}>
              <RecentActivityCard
                entries={recentActivity}
                currency={cur}
                leadPropertyId={reportingScope?.leadPropertyId ?? null}
              />
            </CardSlot>
            {/* Property setup — the persistent "what your PMS gave us" review surface */}
            {propertySetup && (
              <CardSlot delay={200} className="lg:col-span-2">
                <PropertySetupCard
                  propertySetup={propertySetup}
                  propertyBasePath={property.basePath}
                  lastStructureSync={syncStatus?.lastStructureSync ?? null}
                />
              </CardSlot>
            )}
          </div>
        </section>
      </div>
    </OperatorLayout>
  )
}
